import {useState,useRef,useEffect,Suspense} from "react";
import {Box,Autocomplete,TextField,InputAdornment,IconButton,Button,CircularProgress} from '@mui/material';
import {ArrowCircleRight} from '@mui/icons-material';
import {getPickListPopup} from '../../../custom/entities/PickListComponent.js';
import LoadingFallback from '../LoadingFallback.js';
import {getObjectRecords} from '../../integration/ObjectManager.js';
import {DefaultConfig,evaluateReadOnly,consoleLog,applyFilterModel} from '../../../core/utilities/Utilities';
const readOnlyBackgroundColor=DefaultConfig.readOnlyBackgroundColor;

// Autocomplete flavor of FieldTextJoin: instead of a plain read-only text box,
// this renders a client-side-filtered MUI Autocomplete over the full list of the
// related entity's records, loaded once (lazily, only once the field becomes
// editable). Only an explicit selection (typed/inline dropdown, or the
// Select/Update button's pick_list popup) can set the value - free text is
// never accepted.
const FieldTextJoinAutocomplete=({row,rowJoin,recordRules,fieldNameJoin,application,readOnly,userData,setFieldValue,setFieldChanged})=>{
    const [isPickListPopupOpen,setIsPickListPopupOpen]=useState(false);
    const [inputValue,setInputValue]=useState(rowJoin[fieldNameJoin]?.value||'');
    const [selectedOption,setSelectedOption]=useState(
        rowJoin[fieldNameJoin]?.id?{id:rowJoin[fieldNameJoin].id,name:rowJoin[fieldNameJoin].value}:null
    );
    const [options,setOptions]=useState([]);
    const [optionsLoading,setOptionsLoading]=useState(false);
    const hasFetchedRef=useRef(false);

    const fieldName=rowJoin[fieldNameJoin].foreign_key;
    const fieldProp=row?.[fieldName];
    const readOnlyEvaluated=evaluateReadOnly(readOnly,recordRules,fieldProp);
    const isJoinPopulated=rowJoin?.[fieldNameJoin]?.id&&rowJoin?.[fieldNameJoin]?.id!=='';
    const required=isJoinPopulated===undefined?(fieldProp?.required?fieldProp?.required:false):false;
    const PickListPopup=getPickListPopup(rowJoin[fieldNameJoin]?.pick_list);
    const autocompleteEntity=rowJoin[fieldNameJoin]?.autocomplete_entity;
    const autocompleteListName=rowJoin[fieldNameJoin]?.autocomplete_list_name;
    // Optional extra entityParams (e.g. date-range filters) a model's
    // __load_foreign_entities__ config can supply to narrow the fetched option list -
    // evaluated server-side per-request (e.g. relative to "today"), not client-side.
    const autocompleteEntityParams=rowJoin[fieldNameJoin]?.autocomplete_entity_params;
    // Optional: name of ANOTHER foreign-key field on this same record whose current
    // value should also be sent as a filter (e.g. sku_id's config sets this to
    // "batch_id" so the SKU list narrows to that batch's product). When set, the
    // fetch is no longer one-shot - it re-runs whenever the sibling field's value
    // changes, so picking/clearing that field live-narrows this one's options.
    // Absent for every field that doesn't opt in (unchanged one-shot behavior).
    const autocompleteDependsOnField=rowJoin[fieldNameJoin]?.autocomplete_depends_on_field;
    const autocompleteDependsOnValue=autocompleteDependsOnField?row?.[autocompleteDependsOnField]?.value:undefined;
    const fieldLabel=rowJoin?.[fieldNameJoin]?.ui_label_locale?application.getLocale(rowJoin[fieldNameJoin].ui_label_locale):fieldNameJoin;

    // Load the option list the moment the field becomes editable (popup opened for a
    // new record, or "Edit" clicked on an existing one). Without autocompleteDependsOnField
    // this only ever runs once per edit session (hasFetchedRef guards it, exactly as
    // before); with it, it additionally re-runs whenever that sibling field's value changes.
    useEffect(()=>{
        if(readOnlyEvaluated||!autocompleteEntity) return;
        if(!autocompleteDependsOnField&&hasFetchedRef.current) return;
        hasFetchedRef.current=true;
        setOptionsLoading(true);
        (async()=>{
            const dependsOnParam=(autocompleteDependsOnField&&autocompleteDependsOnValue!=null)
                ?[`${autocompleteDependsOnField}=${autocompleteDependsOnValue}`]
                :[];
            const data=await getObjectRecords({
                entityType:autocompleteEntity,
                entityParams:['_lang='+application?.globalData?.lang,...(autocompleteEntityParams||[]),...dependsOnParam],
            });
            if(data&&!('error' in data)){
                const filterModel=autocompleteListName?application?.globalData?.filterModel?.[autocompleteListName]:null;
                setOptions(filterModel?applyFilterModel(data.records||[],filterModel):(data.records||[]));
            }
            else{
                consoleLog({file:'FieldTextJoinAutocomplete.js',context:'loadOptions',arg_name:'error',arg_value:data?.error});
            }
            setOptionsLoading(false);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[readOnlyEvaluated,autocompleteEntity,autocompleteListName,application,autocompleteDependsOnField,autocompleteDependsOnValue]);

    const handleClickDrilldown=()=>{
        const url=rowJoin?.[fieldNameJoin]?.drilldown_link.replace("{ENTITY_ID}", rowJoin?.[fieldNameJoin]?.id);
        window.open(url,'_blank');
    }
    const handleOpenPickListPopup=()=>{
        const pickListPopup=rowJoin[fieldNameJoin]?.pick_list;
        if(pickListPopup){
            setIsPickListPopupOpen(true);
        }
        else{
            consoleLog({file:'FieldTextJoinAutocomplete.js',context:'handleOpenPickListPopup',arg_name:'error',arg_value:'pickListPopup not defined'});
        }
    }

    const pickListCallback=(event)=>{
        if(event.name==='close_popup'){
            setIsPickListPopupOpen(false);
        }
        else if(event.name==='selectRow'){
            setFieldValue(fieldName,event?.id);
            setFieldChanged();
            for(const [key,value] of Object.entries(event?.pickMap)){
                if(key===fieldNameJoin){
                    setSelectedOption({id:event?.id,name:value});
                    setInputValue(value);
                }
            }
            setIsPickListPopupOpen(false);
        }
    }

    const handleChange=(event,newValue)=>{
        // freeSolo is unset (false), so newValue here is always either a matched
        // option object or null - never a raw typed string.
        setSelectedOption(newValue);
        setInputValue(newValue?.name||'');
        setFieldValue(fieldName,newValue?.id??null);
        setFieldChanged();
    };

    const handleInputChange=(event,newInputValue,reason)=>{
        setInputValue(newInputValue);
    };

    return (
        <>
        {isPickListPopupOpen&&
            <Suspense
                fallback={<LoadingFallback/>}>
                <PickListPopup pickListCallback={pickListCallback} application={application} userData={userData} row={row}/>
            </Suspense>}
        <Box sx={{flex:1,minWidth:0,display:'flex',alignItems:'center',gap:1}}>
            {readOnlyEvaluated?(
                <TextField
                    style={{marginTop:"0px",marginBottom:"8px"}}
                    sx={{flex:1,minWidth:0,div:{backgroundColor:readOnlyBackgroundColor}}}
                    InputProps={{
                        readOnly:true,
                        endAdornment:(isJoinPopulated&&
                            <InputAdornment position="end">
                                <IconButton onClick={(e)=>handleClickDrilldown()} color="primary">
                                    <ArrowCircleRight/>
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    id={fieldNameJoin}
                    name={fieldNameJoin}
                    fullWidth
                    variant="outlined"
                    size="small"
                    label={fieldLabel}
                    value={inputValue||''}
                    margin="normal"/>
            ):(
                <Autocomplete
                    sx={{flex:1,minWidth:0}}
                    style={{marginTop:"0px"}}
                    fullWidth
                    size="small"
                    disabled={optionsLoading}
                    options={options}
                    getOptionLabel={(option)=>option?.name||''}
                    isOptionEqualToValue={(option,value)=>option?.id===value?.id}
                    value={selectedOption}
                    inputValue={inputValue}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    noOptionsText={application.getLocale('AutocompleteNoOptions')}
                    id={fieldNameJoin}
                    renderInput={(params)=>(
                        <TextField {...params}
                            style={{marginTop:"0px",marginBottom:"8px"}}
                            required={required}
                            error={fieldProp?.validation_error?fieldProp?.validation_error:false}
                            helperText={fieldProp?.validation_error_message?fieldProp?.validation_error_message:''}
                            name={fieldNameJoin}
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={fieldLabel}
                            placeholder={optionsLoading?application.getLocale('FieldOptionsLoading'):undefined}
                            margin="normal"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment:(
                                    <>
                                    {optionsLoading&&<CircularProgress size={18}/>}
                                    {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />
            )}
            {!readOnlyEvaluated&&
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    sx={{mb:'8px'}}
                    onClick={(e)=>handleOpenPickListPopup()}>{isJoinPopulated?application.getLocale("ButtonUpdate"):application.getLocale("ButtonSelect")}
                </Button>}
        </Box>
        </>
    );
}

export default FieldTextJoinAutocomplete;
